import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowLeft, Ban, Building2, CheckCheck, ExternalLink, Hash, LifeBuoy, Link2, LogOut, LucideIcon, MonitorPlay, MoreHorizontal, PanelRight, Phone, StickyNote, Timer, UserRoundCheck, UserRoundPlus, Users, X, XCircle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFocus } from '../../app/focus';
import { useSocketEvent, useUser } from '../../app/session';
import { toast } from '../../components/toasts';
import { InsightBadges, useActiveInsights, useInsightsFor } from '../../components/InsightBadges';
import { TeamBadge } from '../../components/TeamBadge';
import { Badge, Button, Empty, Spinner } from '../../components/ui';
import { errorMessage, hasPermission, post } from '../../lib/api';
import { contactLabel, formatDuration, formatPhone, statusLabel } from '../../lib/format';
import { useAttendance, useMessages, useTicketLink, ticketLabel } from '../../lib/queries';
import { Composer } from './Composer';
import { CancelModal } from './CancelModal';
import { FinishModal } from './FinishModal';
import { InviteHelpModal } from './InviteHelpModal';
import { LinkCallModal } from './LinkCallModal';
import { Message } from '../../lib/types';
import { MessageList } from './MessageList';
import { SidePanel } from './SidePanel';
import { TakeModal } from './TakeModal';
import { TransferModal } from './TransferModal';
import { typingLabel, useTyping } from '../../app/typing';

/** Segundos em foco desta sessão, atualizados a cada segundo. */
function useElapsed(startedAt: number | null): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!startedAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [startedAt]);
  return startedAt ? Math.max(0, Math.floor((now - startedAt) / 1000)) : 0;
}

export function ConversationView({
  attendanceId,
  onDeselect,
  takeRequested,
  onTakeHandled,
}: {
  attendanceId: string;
  onDeselect: () => void;
  takeRequested: boolean;
  onTakeHandled: () => void;
}) {
  const user = useUser();
  const qc = useQueryClient();
  const { focus, open, close, forget } = useFocus();
  const { data: a, isLoading, error } = useAttendance(attendanceId);
  const messages = useMessages(a?.conversationId ?? null, a?.id ?? null);
  const typing = useTyping(a?.status === 'finalizado' ? null : a?.conversationId);
  const ticketLink = useTicketLink(a?.ticketId);
  // Análise da IA: a atual (em andamento) ou a última, que continua depois de encerrado.
  const activeInsights = useActiveInsights();
  const ownInsight = useInsightsFor(a?.status === 'finalizado' ? [attendanceId] : []);
  const [modal, setModal] = useState<null | 'take' | 'finish' | 'transfer' | 'cancel' | 'invite' | 'link'>(null);
  const [menu, setMenu] = useState(false);
  const [busy, setBusy] = useState(false);

  const mine = a?.status === 'em_atendimento' && a.operatorId === user.id;
  // Ajudando: outro operador é o responsável, eu entrei para ajudar (conto o meu tempo).
  const helping = a?.status === 'em_atendimento' && !mine && Boolean(a.participants?.some((p) => p.userId === user.id));
  const working = mine || helping;
  // Atendimento de ligação: sem WhatsApp, o registro é por anotações internas.
  const voice = a?.channelType === 'voice';
  const canHelp = a?.status === 'em_atendimento' && !working && hasPermission(user, 'attendance:take');
  const takeable = a && (a.status === 'na_fila' || a.status === 'novo' || (a.status === 'transferido' && a.operatorId === user.id));
  const focusedHere = focus?.attendanceId === attendanceId;
  // Transferir: o próprio atendimento, ou (com a opção do cadastro) o de outro operador / ainda não assumido.
  const canTransfer =
    hasPermission(user, 'attendance:transfer') &&
    (mine || (Boolean(user.canTransferOthers) && a !== undefined && ['em_atendimento', 'transferido', 'na_fila'].includes(a.status)));
  const canCancel = Boolean(takeable || mine) && hasPermission(user, 'attendance:take');
  const canLink = mine && voice && a?.ticketDecision === 'pending';
  const canInform = mine && Boolean(a?.ticketId) && !voice;
  const spamItem = mine && hasPermission(user, 'contact:block');
  /** Celular: ações secundárias que vão para o menu "⋮". */
  const mobileMenuItems = canCancel || mine || canInform || canTransfer || canLink || helping;
  const [panelOpen, setPanelOpen] = useState(false);
  /** Mensagem sendo respondida (botão direito → Responder); vale só neste atendimento. */
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  useEffect(() => setReplyTo(null), [a?.id]);
  const elapsed = useElapsed(focusedHere ? focus!.startedAt : null);
  const inCall = focusedHere && focus?.kind === 'voice';

  // Este atendimento de ligação foi vinculado a outro (aqui ou em outra aba): abre o destino.
  const navigate = useNavigate();
  const onAttendanceChanged = useCallback(
    (d: { attendanceId?: string; change?: string; mergedInto?: string }) => {
      if (d.change === 'merged' && d.attendanceId === attendanceId && d.mergedInto) navigate(`/atendimento/${d.mergedInto}`);
    },
    [attendanceId, navigate],
  );
  useSocketEvent('attendance.changed', onAttendanceChanged);

  useEffect(() => {
    if (takeRequested) {
      setModal('take');
      onTakeHandled();
    }
  }, [takeRequested, onTakeHandled]);

  // Abrir na tela = sessão de foco. Ver outro atendimento tira o anterior da tela (o tempo para).
  const autoOpened = useRef(false);
  useEffect(() => {
    if (!a) return;
    if (working && !focusedHere && !autoOpened.current) {
      autoOpened.current = true;
      void open(attendanceId);
    } else if (!working && focus && focus.attendanceId !== attendanceId) {
      void close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a?.id, working]);

  // Marca a conversa como lida ao abrir e quando chegam mensagens novas.
  const lastId = messages.data?.at(-1)?.id;
  useEffect(() => {
    if (!a?.conversationId || !lastId || !working) return;
    void post(`/conversations/${a.conversationId}/read`).then(() => qc.invalidateQueries({ queryKey: ['attendances'] }));
  }, [a?.conversationId, lastId, working, qc]);

  if (isLoading) return <Empty><Spinner /></Empty>;
  if (error || !a) return <Empty>Atendimento não encontrado.</Empty>;

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    try {
      await fn();
      if (ok) toast(ok, 'ok');
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };

  const closeOnScreen = async () => {
    await close();
    onDeselect();
  };

  const informTicket = () =>
    run(async () => {
      await post(`/conversations/${a.conversationId}/ticket-number`);
    }, 'Número do chamado enviado ao cliente');

  const markSpam = () => {
    setMenu(false);
    if (!confirm('Bloquear este número como spam e encerrar o atendimento sem chamado?')) return;
    void run(async () => {
      await post(`/attendances/${a.id}/spam`);
      forget(a.id);
      onDeselect();
    }, 'Número bloqueado');
  };

  const joinHelp = () =>
    run(async () => {
      await post(`/attendances/${a.id}/join`);
      autoOpened.current = true;
      await open(a.id);
      await qc.invalidateQueries({ queryKey: ['attendance', a.id] });
      void qc.invalidateQueries({ queryKey: ['attendances'] });
    }, 'Você entrou para ajudar. As mensagens começam como anotação interna.');

  const leaveHelp = (userId?: string) => {
    const self = !userId || userId === user.id;
    if (!confirm(self ? 'Sair da ajuda neste atendimento? O seu tempo para de contar.' : 'Tirar este operador da ajuda?')) return;
    void run(async () => {
      await post(`/attendances/${a.id}/leave`, self ? {} : { userId });
      if (self) forget(a.id);
      void qc.invalidateQueries({ queryKey: ['attendance', a.id] });
      void qc.invalidateQueries({ queryKey: ['attendances'] });
      if (self) onDeselect();
    }, self ? 'Você saiu da ajuda' : 'Operador retirado da ajuda');
  };

  // Tempo de quem ajuda é só o dele; o total do atendimento soma todos.
  const total = a.totalSeconds + (focusedHere ? elapsed : 0);

  return (
    <div className="group/conv flex h-full">
      {/* Container: o cabeçalho e o compositor se ajustam à largura da coluna (lista e painel abertos ou não), não à da tela. */}
      <div className="@container/chat flex min-w-0 flex-1 flex-col">
        {/* Celular: enquanto digita (teclado aberto), o cabeçalho some para sobrar espaço para a conversa. */}
        <header className="flex min-h-14 shrink-0 flex-wrap items-center gap-2 border-b border-border bg-panel px-2 py-2 max-md:group-has-[textarea:focus]/conv:hidden md:px-4">
          {/* Celular: voltar para a lista (fecha na tela, como o "Fechar"). */}
          <Button variant="ghost" className="md:hidden" onClick={() => (focusedHere ? void closeOnScreen() : onDeselect())} aria-label="Voltar para a lista">
            <ArrowLeft className="size-4" />
          </Button>
          {/* Celular: o contato ocupa a primeira linha (ao lado do voltar) e os botões descem para a segunda.
              Coluna estreita no computador: o contato fica sozinho na primeira linha e os botões descem. */}
          <div className="min-w-0 flex-1 max-md:basis-[calc(100%-4rem)] md:@max-6xl/chat:basis-full">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 @xl/chat:flex-nowrap">
              <span className="truncate font-semibold">{contactLabel(a)}</span>
              {voice && (
                <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-sm font-medium" title="Atendimento aberto por uma ligação">
                  <Phone className="size-3.5" /> Ligação
                </span>
              )}
              <TeamBadge name={a.teamName} size="md" showEmpty={a.status !== 'finalizado' && a.status !== 'novo'} />
              {a.customerName ? (
                <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-accent/15 px-2 py-0.5 text-sm font-semibold text-accent" title="Cliente (empresa) no Agidesk">
                  <Building2 className="size-3.5" />
                  {a.customerName}
                </span>
              ) : (
                <Badge tone="warn">sem cliente</Badge>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-soft">
              <span>{formatPhone(a.contactPhone)}</span>
              <span>·</span>
              <span>{statusLabel(a)}</span>
              {a.operatorName && a.operatorId !== user.id && <span>· com {a.operatorName}</span>}
              {typing && <span className="font-medium text-accent">· {typingLabel(typing)}</span>}
              {(a.participants?.length ?? 0) > 0 && (
                <span className="inline-flex flex-wrap items-center gap-1">
                  · <LifeBuoy className="size-3" /> ajudando:
                  {a.participants.map((p) => (
                    <span key={p.userId} className="inline-flex items-center gap-0.5 rounded bg-warn/15 px-1.5 py-0.5 font-medium text-warn">
                      {p.userId === user.id ? 'você' : p.name}
                      {mine && (
                        <button onClick={() => leaveHelp(p.userId)} className="rounded hover:bg-warn/20" aria-label={`Tirar ${p.name} da ajuda`} title="Tirar da ajuda">
                          <X className="size-3" />
                        </button>
                      )}
                    </span>
                  ))}
                </span>
              )}
              {a.ticketId &&
                (ticketLink ? (
                  <a href={ticketLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 font-medium text-accent hover:underline" title="Abrir o chamado no Agidesk">
                    {ticketLabel(a)}
                    <ExternalLink className="size-3" />
                  </a>
                ) : (
                  <Badge>{ticketLabel(a)}</Badge>
                ))}
              <InsightBadges insight={activeInsights.get(a.id) ?? ownInsight.get(a.id)} />
            </div>
          </div>

          {working && (
            <div
              className={`flex items-center gap-1.5 rounded-md px-2 py-1 font-mono text-sm ${inCall ? 'bg-accent/15 text-accent' : focusedHere ? 'bg-ok/15 text-ok' : 'bg-muted text-fg-soft'}`}
              title={inCall ? 'Em ligação: o tempo conta até desligar' : focusedHere ? 'Contando tempo: o atendimento está aberto na tela' : 'Parado: abra na tela para contar tempo'}
            >
              {inCall ? <Phone className="size-4" /> : <Timer className="size-4" />}
              {inCall && <span className="font-sans text-xs font-semibold">Em ligação</span>}
              {formatDuration(total)}
            </div>
          )}

          {/* Ações secundárias: à vista quando a coluna é larga; quando estreita (e no celular), vão para o menu "⋯" em degraus. */}
          <span className="hidden @5xl/chat:contents">
            {canCancel && (
              <Button variant="ghost" onClick={() => setModal('cancel')} title="Encerrar sem chamado (ex.: o cliente só agradeceu)">
                <XCircle className="size-4" /> Cancelar
              </Button>
            )}
          </span>
          {takeable && (
            <Button variant="primary" onClick={() => setModal('take')}>
              <UserRoundCheck className="size-4" /> Assumir
            </Button>
          )}
          {canHelp && (
            <Button onClick={() => void joinHelp()} disabled={busy} title="Entrar no atendimento para ajudar: o responsável é avisado e o seu tempo conta à parte">
              <LifeBuoy className="size-4" /> Ajudar
            </Button>
          )}
          <span className="hidden @5xl/chat:contents">
            {helping && (
              <Button variant="ghost" onClick={() => leaveHelp()} disabled={busy} title="Sair da ajuda (o seu tempo para de contar)">
                <LogOut className="size-4" /> Sair da ajuda
              </Button>
            )}
            {canLink && (
              <Button variant="ghost" onClick={() => setModal('link')} title="A ligação é de outro atendimento (ex.: cliente ligou de outro número): passa para ele com todo o tempo">
                <Link2 className="size-4" /> Vincular
              </Button>
            )}
            {mine && (
              <Button variant="ghost" onClick={() => setModal('invite')} title="Chamar outro operador para ajudar neste atendimento">
                <UserRoundPlus className="size-4" /> Pedir ajuda
              </Button>
            )}
          </span>
          {working && !focusedHere && (
            <Button onClick={() => void open(a.id)} title="Abrir na tela e voltar a contar tempo">
              <MonitorPlay className="size-4" /> <span className="hidden sm:inline">Abrir na tela</span>
            </Button>
          )}
          <span className="hidden @2xl/chat:contents">
            {canInform && (
              <Button onClick={() => void informTicket()} disabled={busy} title="Envia ao cliente o número e o link do chamado">
                <Hash className="size-4" /> Informar chamado
              </Button>
            )}
          </span>
          <span className="hidden @lg/chat:contents">
            {canTransfer && (
              <Button onClick={() => setModal('transfer')}>
                <Users className="size-4" /> Transferir
              </Button>
            )}
          </span>
          {mine && (
            <Button variant="primary" onClick={() => setModal('finish')}>
              <CheckCheck className="size-4" /> Finalizar
            </Button>
          )}
          {/* Painel do contato: coluna fixa a partir de 1024 px; abaixo disso, tela por cima. */}
          <Button variant="ghost" className="lg:hidden" onClick={() => setPanelOpen(true)} aria-label="Detalhes do contato" title="Contato, chamado e linha do tempo">
            <PanelRight className="size-4" />
          </Button>
          {(mobileMenuItems || spamItem) && (
            <div
              className={clsx(
                'relative',
                // Some quando todos os itens dele já estão à vista no cabeçalho.
                !spamItem && (canCancel || mine || canLink || helping ? '@5xl/chat:hidden' : canInform ? '@2xl/chat:hidden' : '@lg/chat:hidden'),
              )}
            >
              <Button variant="ghost" onClick={() => setMenu((m) => !m)} aria-label="Mais ações">
                <MoreHorizontal className="size-4" />
              </Button>
              {menu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setMenu(false)} />
                  <div className="absolute right-0 z-20 mt-1 w-56 rounded-md border border-border bg-panel py-1 shadow-lg">
                    {/* Cada item some do menu quando o botão dele aparece no cabeçalho (mesmos degraus). */}
                    <div className="@5xl/chat:hidden">
                      {canCancel && <MenuItem icon={XCircle} label="Cancelar (sem chamado)" onClick={() => (setMenu(false), setModal('cancel'))} />}
                      {mine && <MenuItem icon={UserRoundPlus} label="Pedir ajuda" onClick={() => (setMenu(false), setModal('invite'))} />}
                      {canInform && <MenuItem icon={Hash} label="Informar chamado" className="@2xl/chat:hidden" onClick={() => (setMenu(false), void informTicket())} />}
                      {canTransfer && <MenuItem icon={Users} label="Transferir" className="@lg/chat:hidden" onClick={() => (setMenu(false), setModal('transfer'))} />}
                      {canLink && <MenuItem icon={Link2} label="Vincular a outro atendimento" onClick={() => (setMenu(false), setModal('link'))} />}
                      {helping && <MenuItem icon={LogOut} label="Sair da ajuda" onClick={() => (setMenu(false), leaveHelp())} />}
                    </div>
                    {spamItem && (
                      <button className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-danger hover:bg-muted" onClick={() => (setMenu(false), markSpam())}>
                        <Ban className="size-4" /> Bloquear número (spam)
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
          <span className="hidden md:contents">
            {focusedHere ? (
              <Button onClick={() => void closeOnScreen()} title="Fecha o atendimento na tela e para o cronômetro (Esc)" aria-label="Fechar">
                <X className="size-4" /> <span className="hidden @md/chat:inline">Fechar</span>
              </Button>
            ) : (
              <Button variant="ghost" onClick={onDeselect} aria-label="Fechar visualização">
                <X className="size-4" />
              </Button>
            )}
          </span>
        </header>

        <div className="min-h-0 flex-1">
          <MessageList
            messages={messages.data ?? []}
            loading={messages.isLoading}
            attendanceId={a.id}
            viewer={{ userId: user.id, ownerId: a.operatorId, helperIds: a.participants?.map((p) => p.userId) ?? [] }}
            onReply={a.status !== 'finalizado' && working && !voice ? setReplyTo : undefined}
          />
        </div>

        {a.status !== 'finalizado' ? (
          <Composer
            key={`${a.id}:${mine ? 'owner' : helping ? 'helper' : 'viewer'}`}
            attendance={a}
            canReply={working && !voice}
            startAsNote={!mine || voice}
            voice={voice}
            replyTo={replyTo}
            onClearReply={() => setReplyTo(null)}
          />
        ) : (
          <div className="border-t border-border bg-panel px-4 py-3 text-sm text-fg-soft">
            <StickyNote className="mr-1 inline size-4" />
            Atendimento finalizado{a.finishedAt ? ` em ${new Date(a.finishedAt).toLocaleString('pt-BR')}` : ''}. Uma nova mensagem do cliente abre outro atendimento na fila.
          </div>
        )}
      </div>

      <div className="hidden lg:flex">
        <SidePanel attendance={a} />
      </div>
      {panelOpen && (
        <div className="fixed inset-0 z-40 flex justify-end bg-black/30 lg:hidden" onClick={() => setPanelOpen(false)}>
          <div className="flex h-full w-full flex-col bg-panel shadow-xl md:w-96" onClick={(e) => e.stopPropagation()}>
            <div className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-2">
              <Button variant="ghost" onClick={() => setPanelOpen(false)} aria-label="Voltar para a conversa">
                <ArrowLeft className="size-4" />
              </Button>
              <span className="truncate font-medium">{contactLabel(a)}</span>
            </div>
            <SidePanel attendance={a} className="min-h-0 w-full flex-1" />
          </div>
        </div>
      )}

      {modal === 'take' && <TakeModal attendanceId={a.id} onClose={() => setModal(null)} />}
      {modal === 'invite' && <InviteHelpModal attendance={a} onClose={() => setModal(null)} />}
      {modal === 'link' && <LinkCallModal attendance={a} onClose={() => setModal(null)} />}
      {modal === 'finish' && (
        <FinishModal
          attendance={a}
          onClose={() => setModal(null)}
          onFinished={() => {
            forget(a.id);
            setModal(null);
            onDeselect();
          }}
        />
      )}
      {modal === 'cancel' && (
        <CancelModal
          attendanceId={a.id}
          onClose={() => setModal(null)}
          onCancelled={() => {
            forget(a.id);
            setModal(null);
            onDeselect();
          }}
        />
      )}
      {modal === 'transfer' && (
        <TransferModal
          attendance={a}
          onClose={() => setModal(null)}
          onTransferred={() => {
            forget(a.id);
            setModal(null);
            onDeselect();
          }}
        />
      )}
      <EscToClose enabled={focusedHere && modal === null} onClose={() => void closeOnScreen()} />
    </div>
  );
}

/** Atalho Esc = "Fechar" (só sem modal aberto). */
function MenuItem({ icon: Icon, label, onClick, className }: { icon: LucideIcon; label: string; onClick: () => void; className?: string }) {
  return (
    <button className={clsx('flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted', className)} onClick={onClick}>
      <Icon className="size-4" /> {label}
    </button>
  );
}

function EscToClose({ enabled, onClose }: { enabled: boolean; onClose: () => void }) {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !(e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement)) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, onClose]);
  return null;
}

